// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import { css, cx } from '@linaria/core';

import { themeCssVar as globalTheme } from '../../utils/theme';

export const theme = {
  background: globalTheme.gradient.backgroundButtonPrimary,
  backgroundDisabled: globalTheme.color.backgroundButtonDisabled,
  border: 'transparent',
  borderDisabled: 'transparent',
  focus: globalTheme.color.borderFocusDefault,
  foreground: globalTheme.color.foregroundButtonPrimary,
  foregroundDisabled: globalTheme.color.foregroundButtonDisabled,
  overlay: globalTheme.color.interactiveButtonPrimary,
  overlayDisabled: 'transparent',
  heightLarge: '2.6rem',
  heightMedium: '2.6rem',
  heightSmall: '2.0rem',
  radius: globalTheme.sizing.buttonBorderRadius,
  paddingGap: globalTheme.sizing.paddingSmall,
  paddingHorizontalLarge: globalTheme.sizing.buttonLargePaddingHorizontal,
  paddingHorizontalMedium: globalTheme.sizing.buttonMediumPaddingHorizontal,
  paddingHorizontalSmall: globalTheme.sizing.buttonSmallPaddingHorizontal,
  paddingVerticalLarge: globalTheme.sizing.buttonLargePaddingVertical,
  paddingVerticalMedium: globalTheme.sizing.buttonMediumPaddingVertical,
  paddingVerticalSmall: globalTheme.sizing.buttonSmallPaddingVertical,
  fontFamily: globalTheme.fontFamily.button1,
  fontSize: globalTheme.fontSize.button1,
  fontWeight: globalTheme.fontWeight.button1,
  lineHeight: globalTheme.lineHeight.button1,
  letterSpacing: globalTheme.letterSpacing.button1,
  textTransform: globalTheme.letterCasing.button1,
  fontSizeSmall: globalTheme.fontSize.button2,
  fontWeightSmall: globalTheme.fontWeight.button2,
  lineHeightSmall: globalTheme.lineHeight.button2,
  letterSpacingSmall: globalTheme.letterSpacing.button2,
  textTransformSmall: globalTheme.letterCasing.button2,
};

const cssVariable = {
  background: `--tui-button-background`,
  foreground: `--tui-button-foreground`,
  border: `--tui-button-border`,
  overlay: `--tui-button-overlay`,
  focus: `--tui-button-focus`,
  backgroundDisabled: `--tui-button-background-disabled`,
  foregroundDisabled: `--tui-button-foreground-disabled`,
  borderDisabled: `--tui-button-border-disabled`,
  overlayDisabled: `--tui-button-overlay-disabled`,
};

const container = css`
  /* Block */
  display: inline-block;
  position: relative;
  border: 0;
  background-color: transparent;
  cursor: pointer;

  /* Variables */
  ${`${cssVariable.background}: ${theme.background};`}
  ${`${cssVariable.foreground}: ${theme.foreground};`}
  ${`${cssVariable.border}: ${theme.border};`}
  ${`${cssVariable.overlay}: ${theme.overlay};`}
  ${`${cssVariable.focus}: ${theme.focus};`}
  ${`${cssVariable.backgroundDisabled}: ${theme.backgroundDisabled};`}
  ${`${cssVariable.foregroundDisabled}: ${theme.foregroundDisabled};`}
  ${`${cssVariable.borderDisabled}: ${theme.borderDisabled};`}
  ${`${cssVariable.overlayDisabled}: ${theme.overlayDisabled};`}

  /* Modifiers */
  &[data-disabled] {
    cursor: default;
  }
`;

const touchTarget = css`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  min-width: 48px;
  min-height: 48px;
  transform: translate(-50%, -50%);
  background-color: transparent;
  opacity: 0;
`;

const wrapper = css`
  display: block;
  position: relative;
`;

const content = css`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  gap: ${theme.paddingGap};
  padding: ${theme.paddingVerticalLarge} ${theme.paddingHorizontalLarge};

  /* Modifiers */
  .${container}[data-size="medium"] & {
    padding: ${theme.paddingVerticalMedium} ${theme.paddingHorizontalMedium};
  }

  .${container}[data-size="small"] & {
    padding: ${theme.paddingVerticalSmall} ${theme.paddingHorizontalSmall};
  }

  .${container}[data-dir="rtl"] & {
    flex-direction: row-reverse;
  }

  .${container}[data-loading] & {
    opacity: 0;
  }
`;

const commonOverlay = css`
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: ${theme.radius};
`;

const loader = css`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(${cssVariable.foreground});
`;

const focus = css`
  opacity: 0;
  margin: -2px;
  box-shadow: 0 0 0 4px var(${cssVariable.focus});

  .${container}[data-focused] & {
    opacity: 1;
  }
`;

const fill = css`
  background: var(${cssVariable.background});
  border: 2px solid var(${cssVariable.border});
  background-clip: border-box;
  background-origin: border-box;

  .${container}[data-disabled] & {
    background: var(${cssVariable.backgroundDisabled});
    border-color: var(${cssVariable.borderDisabled});
  }
`;

const label = css`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  color: var(${cssVariable.foreground});
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize};
  font-weight: ${theme.fontWeight};
  line-height: ${theme.lineHeight};
  letter-spacing: ${theme.letterSpacing};
  text-transform: ${theme.textTransform};
  text-align: center;
  word-break: break-word;
  min-height: ${theme.heightLarge};

  /* Modifiers */
  .${container}[data-size="medium"] & {
    min-height: ${theme.heightMedium};
  }

  .${container}[data-size="small"] & {
    font-size: ${theme.fontSizeSmall};
    font-weight: ${theme.fontWeightSmall};
    line-height: ${theme.lineHeightSmall};
    letter-spacing: ${theme.letterSpacingSmall};
    text-transform: ${theme.textTransformSmall};
    min-height: ${theme.heightSmall};
  }

  .${container}[data-disabled] & {
    color: var(${cssVariable.foregroundDisabled});
  }
`;

const overlay = css`
  opacity: 0;
  background-color: var(${cssVariable.overlay});

  .${container}[data-disabled] & {
    color: var(${cssVariable.overlayDisabled});
  }

  .${container}[data-hovered] &,
  .${container}[data-tapped] &,
  .${container}:active & {
    opacity: 1;
  }
`;

const iconLeading = css`
  display: flex;
  align-items: flex;
  justify-content: center;
  position: relative;
`;

const iconTrailing = css`
  display: flex;
  align-items: flex;
  justify-content: center;
  position: relative;
`;

const styles = {
  container,
  content,
  touchTarget,
  loader: cx(commonOverlay, loader),
  wrapper,
  focus: cx(commonOverlay, focus),
  fill: cx(commonOverlay, fill),
  label,
  overlay: cx(commonOverlay, overlay),
  iconLeading,
  iconTrailing,
};

export default styles;
